import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-safety';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Is AllChinaBuySpreadsheet Safe? Full Verification Process Explained',
    description: 'Complete safety guide explaining the multi-point verification system behind AllChinaBuySpreadsheet. Learn how scam-filtered data, supplier checks, and review authenticity analysis protect buyers.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Safety Guide', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Security%20professional%20reviewing%20documents%20with%20magnifying%20glass%20in%20modern%20office%2C%20clean%20corporate%20setting%2C%20bright%20natural%20lighting%2C%20trust%20and%20verification%20theme%2C%20professional%20photography%2C%20organized%20desk%2C%20glass%20walls%2C%20confident%20atmosphere&width=1800&height=840&seq=safety-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet safety guide"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Safety Guide</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">Is AllChinaBuySpreadsheet Safe? Full Verification Process Explained</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Safety is the number one concern for anyone buying products from China online. With countless stories of
            counterfeit goods, shipping scams, and unreliable suppliers circulating on forums, it is natural to ask:
            is <strong className="text-primary-600 font-semibold">AllChinaBuySpreadsheet</strong> actually safe to use?
            The short answer is yes — and this guide explains exactly why, detailing every layer of our verification process.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Our Multi-Point Verification System</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Every product that appears in our <strong className="text-primary-600 font-semibold">china product spreadsheet list</strong> passes
            through a rigorous screening process. First, automated systems scan for pricing anomalies — if a product is priced
            significantly below market average, it is flagged for manual review. Second, supplier histories are cross-referenced
            against multiple databases to verify business registration and track record. Third, review authenticity analysis
            uses pattern recognition to identify fake or purchased reviews.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Only after passing all three automated checks does a listing reach our human verification team. These specialists
            manually inspect product images, compare descriptions against actual specifications, and verify shipping claims.
            The <strong className="text-primary-600 font-semibold">allchinabuy spreadsheet</strong> methodology prioritizes
            transparency — every verified listing includes a timestamp showing when it was last checked.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Scam-Filtered Data: How It Works</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Our scam-filtering algorithm is continuously updated based on reported incidents and emerging fraud patterns.
            When a new scam technique is identified — such as bait-and-switch listings or fake tracking numbers — the filter
            is updated within 24 hours. Listings that trigger the filter are immediately removed from all active spreadsheets
            and flagged for investigation. This proactive approach has prevented thousands of potentially fraudulent transactions.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Ready to Shop Safely?</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Visit KASpreadsheet <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-guide" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Shopping Guide: Save 70% on China Products &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-quality" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Quality Control: How Products Are Verified &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}